/**
 * DeductionPage Component
 * 앱 전용 일차감 페이지 - VROONG 브랜드 통일 디자인
 */

import React, { useRef, KeyboardEvent, useMemo } from 'react';
import BottomNavigation from '../../../core/components/BottomNavigation';
import CustomSelect, { SelectOption } from '../../../core/components/CustomSelect';
import { useDeduction } from '../hooks/useDeduction';
import { useDeferredLoading } from '../../../core/hooks/useDeferredLoading';
import { DeductionItem } from '../types/deduction';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/DeductionPage.module.css';
import { getUserInfo } from '../../../core/utils';

const DeductionPage: React.FC = () => {
    // Hook 사용
    const {
        deductionList,
        filteredDeductionList,
        fromDate,
        toDate,
        deductTypeCodes,
        deductType,
        isLoading: rawIsLoading,
        error,
        isSupplier,
        handleFromDateChange,
        handleToDateChange,
        handleDeductTypeChange,
    } = useDeduction();

    // 지연 로딩 적용 (200ms 이상 로딩 시에만 로딩 UI 표시)
    const isLoading = useDeferredLoading(rawIsLoading, 200);

    // deductTypeCodes를 SelectOption 형식으로 변환
    const deductTypeOptions: SelectOption[] = useMemo(() => {
        const options: SelectOption[] = [{ value: '', label: '전체' }];
        if (deductTypeCodes) {
            deductTypeCodes.forEach((code) => {
                options.push({
                    value: code.comCode,
                    label: code.comCdName,
                });
            });
        }
        return options;
    }, [deductTypeCodes]);

    // 데이트피커 참조
    const fromDateRef = useRef<HTMLInputElement>(null);
    const toDateRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const handleItemSelect = (item: DeductionItem) => {
        const seqNo = item.deductSeqNo || item.seqNo;
        if (!seqNo) return;

        const params = new URLSearchParams({
            seqNo: String(seqNo),
        });

        navigate(`/deduction/detail?${params.toString()}`);
    };

    const handleItemKeyDown = (event: KeyboardEvent<HTMLDivElement>, item: DeductionItem) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleItemSelect(item);
        }
    };

    // 데이트피커 아이콘 클릭 핸들러
    const handleFromDateIconClick = () => {
        if (fromDateRef.current) {
            fromDateRef.current.showPicker();
        }
    };

    const handleToDateIconClick = () => {
        if (toDateRef.current) {
            toDateRef.current.showPicker();
        }
    };

    // 타입별 아이콘
    const getTypeIcon = (deductType: string): string => {
        switch (deductType) {
            case 'RENT':
                return '🚗';
            case 'LEASE':
                return '📋';
            case 'ETC':
                return '📦';
            default:
                return '📄';
        }
    };

    // 타입별 라벨
    const getTypeLabel = (deductType: string): string => {
        switch (deductType) {
            case 'RENT':
                return '렌트';
            case 'LEASE':
                return '리스';
            case 'ETC':
                return '기타';
            default:
                return '기타';
        }
    };

    // 타입별 CSS 클래스
    const getTypeClass = (deductType: string): string => {
        switch (deductType) {
            case 'RENT':
                return 'rental';
            case 'LEASE':
                return 'lease';
            case 'ETC':
                return 'etc';
            default:
                return 'etc';
        }
    };

    return (
        <div className={`screen active ${styles['deduction-screen']}`}>
            {/* 헤더 */}
            <div className={styles['deduction-header']}>
                <div className={styles['header-top']}>
                    <h1 className={styles['page-title']}>일차감 내역</h1>
                </div>

                {/* 필터 섹션 */}
                <div className={styles['filter-section']}>
                    <div className={styles['filter-row']}>
                        <CustomSelect
                            options={deductTypeOptions}
                            value={deductType}
                            onChange={handleDeductTypeChange}
                            placeholder="전체"
                        />
                    </div>

                    <div className={styles['date-range-wrapper']}>
                        <div className={styles['date-input-box']}>
                            <input
                                ref={fromDateRef}
                                type="date"
                                className={styles['date-input']}
                                value={fromDate}
                                onChange={(e) => handleFromDateChange(e.target.value)}
                                onKeyDown={(e) => e.preventDefault()}
                            />
                            <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={handleFromDateIconClick}></i>
                        </div>
                        <span className={styles['date-separator']}>~</span>
                        <div className={styles['date-input-box']}>
                            <input
                                ref={toDateRef}
                                type="date"
                                className={styles['date-input']}
                                value={toDate}
                                onChange={(e) => handleToDateChange(e.target.value)}
                                onKeyDown={(e) => e.preventDefault()}
                            />
                            <i className={`fas fa-calendar-alt ${styles['date-icon']}`} onClick={handleToDateIconClick}></i>
                        </div>
                    </div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['deduction-container']}>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']}></div>
                        <p className={styles['loading-text']}>일차감 내역을 불러오는 중...</p>
                    </div>
                ) : error ? (
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>데이터를 불러오는 중 오류가 발생했습니다.</p>
                    </div>
                ) : !filteredDeductionList || filteredDeductionList.length === 0 ? (
                    <div className={styles['empty-section']}>
                        <div className={styles['empty-icon']}>📭</div>
                        <p className={styles['empty-text']}>일차감 내역이 없습니다.</p>
                    </div>
                ) : (
                    <div className={styles['deduction-list']}>
                        {filteredDeductionList.map((item) => {
                            const typeClass = getTypeClass(item.deductType || '');
                            const isExpired = item.terminatedYn !== 'N';

                            return (
                                <div
                                    key={item.seqNo ?? `${item.deductType}-${item.calcType}-${item.startDay}`}
                                    className={`${styles['deduction-item']} ${styles[typeClass]} ${isExpired ? styles['expired'] : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleItemSelect(item)}
                                    onKeyDown={(event) => handleItemKeyDown(event, item)}
                                >
                                    <div className={styles['deduction-top-row']}>
                                        <div className={`${styles['deduction-type-pill']} ${styles[typeClass]}`}>
                                            <span>{getTypeIcon(item.deductType || '')}</span>
                                            <span>{getTypeLabel(item.deductType || '')}</span>
                                        </div>
                                        <span className={`${styles['deduction-status']} ${isExpired ? styles['expired'] : styles['active']}`}>
                                            {isExpired ? '⏸️ 종료' : '✅ 진행중'}
                                        </span>
                                    </div>
                                    <div className={styles['deduction-middle-row']}>
                                        <div className={styles['deduction-name']}>{item.deductName}</div>
                                        <div className={styles['deduction-amount']}>
                                            {item.calcType === '1'
                                                ? `${(item.deductAmt || 0).toLocaleString()} 원`
                                                : item.deductAmt
                                                ? `${(item.deductAmt || 0).toLocaleString()} 원`
                                                : `${(item.deductRate || 0)}%`
                                            }
                                        </div>
                                    </div>
                                    <div className={styles['deduction-date-row']}>
                                        {item.startDay && <span>시작 {item.startDay}</span>}
                                        {item.endDay && <span>종료 {item.endDay}</span>}
                                    </div>
                                    <div className={styles['deduction-date-row']}>
                                        {item.tranDay && (
                                            <span>집계 {`${item.tranDay.slice(0, 4)}-${item.tranDay.slice(4, 6)}-${item.tranDay.slice(6, 8)}`}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 하단 네비게이션 */}
            <BottomNavigation currentScreen="deduction" />
        </div>
    );
};

export default DeductionPage;
